// Cost rollups over currency fields. Pure — no I/O. Amounts are integer minor
// units (H-7) so summation is exact. Mixed ISO codes are never added together.

import type { ActivityLog, ActivityType } from "./types.js";
import { localCalendarDate } from "./datetime.js";

export interface TypeSpend {
  activityTypeId: string;
  name: string;
  minorUnits: number;
}

export interface PeriodSpend {
  period: string;
  minorUnits: number;
}

export interface CostRollup {
  currency: string;
  byType: TypeSpend[];
  byMonth: PeriodSpend[];
  byYear: PeriodSpend[];
}

function add(map: Map<string, number>, key: string, amount: number): void {
  map.set(key, (map.get(key) ?? 0) + amount);
}

function periodRows(map: Map<string, number>): PeriodSpend[] {
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, minorUnits]) => ({ period, minorUnits }));
}

/**
 * Sum every currency-kind field on `logs` using each log's type schema.
 * Returns one group per ISO currency code, sorted by code.
 */
export function costRollup(
  logs: ActivityLog[],
  types: ActivityType[],
  timezone: string,
): CostRollup[] {
  const typeById = new Map(types.map((t) => [t.activityTypeId, t]));
  const groups = new Map<
    string,
    { byType: Map<string, number>; byMonth: Map<string, number>; byYear: Map<string, number> }
  >();

  for (const log of logs) {
    const type = typeById.get(log.activityTypeId);
    if (!type) continue;
    const day = localCalendarDate(log.occurredAt, timezone);
    const month = day.slice(0, 7);
    const year = day.slice(0, 4);
    for (const field of type.fields) {
      if (field.kind !== "currency") continue;
      const raw = log.values[field.fieldId];
      if (typeof raw !== "number" || !Number.isFinite(raw)) continue;
      const amount = Math.round(raw);
      const currency = (field.unit ?? "USD").toUpperCase();
      let group = groups.get(currency);
      if (!group) {
        group = { byType: new Map(), byMonth: new Map(), byYear: new Map() };
        groups.set(currency, group);
      }
      add(group.byType, type.activityTypeId, amount);
      add(group.byMonth, month, amount);
      add(group.byYear, year, amount);
    }
  }

  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, group]) => ({
      currency,
      byType: [...group.byType.entries()]
        .map(([activityTypeId, minorUnits]) => ({
          activityTypeId,
          name: typeById.get(activityTypeId)?.name ?? activityTypeId,
          minorUnits,
        }))
        .sort((a, b) => b.minorUnits - a.minorUnits || a.name.localeCompare(b.name)),
      byMonth: periodRows(group.byMonth),
      byYear: periodRows(group.byYear),
    }));
}

export function rollupTotal(group: CostRollup): number {
  return group.byType.reduce((sum, row) => sum + row.minorUnits, 0);
}
